import { useDispatch, useSelector } from "react-redux";

import type { Feature, MultiPolygon, Polygon } from "geojson";

import { useUploadAssetMutation } from "@/api/assets/useAssetsApi";
import { buildCoverageTypePayload } from "@/components/spatialAnalysis/spatialOutputAnalysis/utils";
import {
  removeStartFromUnderscore,
  stringArrayToNumberArray
} from "@/helpers/string";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import type { RootState } from "@/store";
import { applyLegendImageAssetIds } from "@/store/slice/datasetSlice";
import { type Dataset, ProportionMethodOutput } from "@/types/dataset";
import type {
  AnalysisOutput,
  AreaInput,
  Catchment,
  DatasetConfigAttributeResponse,
  DatasetConfigResponse
} from "@/types/project";
import {
  AreaInputType,
  type CatchmentDataset,
  ECatchmentInputBy,
  ECatchmentTransport,
  ECatchmentType,
  EOutputAnalysisType,
  EOutputGridType,
  ERoadAccessMeasurement,
  ScoringOptionsEnum
} from "@/types/spatial";
import { AggregationType, WidgetDefaultConfiguration } from "@/types/widget";
import { getEffectiveRoadExclusions, iterateRows } from "@/utils/catchment";
import {
  createFinalAttributes,
  getGridConfig,
  getMaxLevelAdminArea,
  mapDatasetFilters,
  type MappedFilter,
  mapStyleToLegend,
  processCatchmentDatasetAttributes,
  separateAdminAreaAttributes
} from "@/utils/configurationMapper";
import { convertDatasetResponseToFeatureCollection } from "@/utils/dataset";
import {
  getDatasetAttributeScoringId,
  getPrimaryPoiAttribute,
  isNumericAttribute
} from "@/utils/datasetAttributes";
import {
  applyRecentMarkerImageAssetIds,
  dataUrlToBlob,
  isLocalImageUrl
} from "@/utils/mapLegend";
import { parseFormattedNumber } from "@/utils/number";
import {
  getIsIncludedScoring,
  getNegativeImpactValue,
  parseWeightValue,
  parseZScore
} from "@/utils/weightValues";

import { pointOnFeature } from "@turf/turf";

const useConfigurationMappers = () => {
  const {
    isAdminTreeEnabled,
    isBVTDataForCatchmentEnabled,
    isDatasetWidgetEnabled,
    isCoverageTypeEnabled,
    isCustomMapVisualizationLegendEnabled,
    isDasimetryEnabled,
    isNewRoadExclusionEnabled
  } = useAppFeatures();
  const dispatch = useDispatch();
  const { mutateAsync: uploadAssetAsync } = useUploadAssetMutation();
  const {
    datasets: currentDatasets,
    filters: datasetFilters,
    visualizationOrder: currentVisualizationOrder,
    weightValues,
    history
  } = useSelector((state: RootState) => state.dataset);

  const {
    widgets: currentWidgets,
    visualizationOrder: widgetVisualizationOrder,
    history: historyWidgets
  } = useSelector((state: RootState) => state.widget);

  const {
    adminAreaSettings,
    adminTreeSettings,
    catchmentSettings,
    spatialConfig,
    polygonSettings,
    overrideGlobalFilter
  } = useSelector((state: RootState) => state.spatial);
  const { showAnalysisResultPopup } = useSelector(
    (state: RootState) => state.ui
  );
  const {
    adminAreaSettings: globalAdminAreaSettings,
    selectedAreaIds: globalSelectedAreaIds
  } = useSelector((state: RootState) => state.globalFilter);

  /* Check if the analysis result popup is open, will get the datasets from history
    Reference: file useAnalysisResult.tsx line 49
  */
  const datasets = showAnalysisResultPopup ? history.datasets : currentDatasets;
  const visualizationOrder = showAnalysisResultPopup
    ? history.visualizationOrder
    : currentVisualizationOrder;

  const widgets = showAnalysisResultPopup
    ? historyWidgets.widgets
    : currentWidgets;
  const visualizationOrderWidget = showAnalysisResultPopup
    ? historyWidgets.visualizationOrder
    : widgetVisualizationOrder;

  // Build a DatasetConfigResponse from a single catchment dataset entry —
  // shared by both the legacy `dataset` (singular) and the new `datasets`
  // (array) payload field.
  const buildCatchmentDatasetConfig = (
    entry: CatchmentDataset
  ): DatasetConfigResponse => ({
    id: entry.rawDataset?.dataset_id,
    name: entry.rawDataset?.name,
    display_name: entry.dataset?.data?.display_name,
    spatial_aggregation_type: entry.rawDataset?.spatial_aggregation_type,
    attributes: processCatchmentDatasetAttributes(entry.dataset, entry.filters),
    color_based: entry.dataset?.selectedCategory ?? undefined,
    label_config: {
      ...entry.dataset?.style?.labelConfig,
      enable_ranking: entry.dataset?.style?.rankConfig?.isEnabled ?? false,
      max_ranking: entry.dataset?.style?.rankConfig?.maxRanking ?? 10
    },
    weight: parseWeightValue(
      weightValues[entry.rawDataset?.dataset_id]?.weight
    ),
    is_global_admin_area: entry.dataset?.is_global_admin_area ?? false
  });

  const mapCatchmentAreaInput = (): AreaInput | null => {
    if (!catchmentSettings) return null;
    const {
      catchmentType,
      roadAccessConfig,
      radiusData = [],
      placeCoordinates
    } = catchmentSettings;
    const byRadius = catchmentType === ECatchmentType.BY_RADIUS;
    const isDistance =
      !byRadius &&
      roadAccessConfig?.measurementType === ERoadAccessMeasurement.DISTANCE;

    const inputByLocation =
      catchmentSettings.catchmentInputBy === ECatchmentInputBy.BY_LOCATION;
    const inputByDataset =
      catchmentSettings.catchmentInputBy === ECatchmentInputBy.BY_DATASET;
    const hasCoordinates = placeCoordinates && placeCoordinates.length > 0;
    const catchmentByDataset = Object.values(
      catchmentSettings.datasetConfig?.datasets ?? {}
    );
    const isochroneData = roadAccessConfig?.isochroneData;

    let locations: Catchment["locations"] = undefined;

    const isPolygonCatchmentDataset =
      isBVTDataForCatchmentEnabled &&
      inputByDataset &&
      catchmentByDataset.some(
        (entry) => entry.dataset?.data?.spatial_aggregation_type === "polygon"
      );

    if (inputByLocation && hasCoordinates) {
      locations = placeCoordinates.map((place, index) => ({
        name: place.locationName || `Marker #${index + 1}`,
        coordinates: {
          latitude: place.lat,
          longitude: place.lng
        },
        catchment_area: isochroneData?.[index]?.data ?? null // send isochrone data instead of null, because on API analysis/:id/config does not return isochrone data
      }));
    } else if (isPolygonCatchmentDataset) {
      /** Polygon catchment dataset uses the same { dataset(s), locations }
       * shape as POI catchment dataset. Each polygon row → one Location
       * whose `coordinates` is the polygon centroid and `catchment_area`
       * carries the row's geometry (Polygon or MultiPolygon) as a
       * FeatureCollection. When the multi-dataset flag is on, every
       * location carries a `dataset_id` linking it back to its source
       * dataset.
       */
      const featureEntries = catchmentByDataset
        .filter(
          (entry) => entry.dataset?.data?.spatial_aggregation_type === "polygon"
        )
        .flatMap((entry) =>
          convertDatasetResponseToFeatureCollection(entry.rawDataset)
            .features.filter(
              (feature) =>
                feature.geometry?.type === "Polygon" ||
                feature.geometry?.type === "MultiPolygon"
            )
            .map((feature) => ({
              geometry: feature.geometry as Polygon | MultiPolygon,
              datasetId: entry.rawDataset?.dataset_id
            }))
        );
      locations = featureEntries.map(({ geometry, datasetId }, index) => {
        const polygonSeq = index + 1;
        const polygonFeature: Feature<Polygon | MultiPolygon> = {
          type: "Feature",
          geometry,
          properties: {
            name: `Location layer polygon #${polygonSeq}`,
            sequence: polygonSeq
          }
        };
        const [lng, lat] = pointOnFeature(polygonFeature).geometry.coordinates;
        return {
          name: `Location layer polygon #${polygonSeq}`,
          coordinates: { latitude: lat, longitude: lng },
          catchment_area: [
            {
              type: "FeatureCollection",
              features: [polygonFeature]
            }
          ],
          ...(isBVTDataForCatchmentEnabled && datasetId
            ? { dataset_id: datasetId }
            : {})
        };
      });
    } else if (inputByDataset && !byRadius && catchmentByDataset.length > 0) {
      /** POI dataset rows → Location[]. Iterate per dataset so we can stamp
       * each location with its `dataset_id` when the multi-dataset flag is
       * enabled, while preserving the global isochrone index used by the
       * existing single-dataset payload.
       */
      const locationResults: Catchment["locations"] = [];
      let globalIndex = 0;
      for (const entry of catchmentByDataset) {
        const datasetId = entry.rawDataset?.dataset_id;
        iterateRows([entry], (long, lat, label) => {
          locationResults.push({
            name: label ?? "",
            coordinates: { latitude: lat, longitude: long },
            catchment_area: isochroneData?.[globalIndex]?.data ?? null,
            ...(isBVTDataForCatchmentEnabled && datasetId
              ? { dataset_id: datasetId }
              : {})
          });
          globalIndex += 1;
        });
      }
      locations = locationResults;
    }

    const hasCatchmentDatasets =
      inputByDataset && catchmentByDataset.length > 0;
    const useMultiDataset =
      isBVTDataForCatchmentEnabled && hasCatchmentDatasets;

    /** Polygon catchment dataset hides the catchment-type section in the UI,
     * so radius/road config never gets set. BE still validates contours
     * (must be 1+ items in range 1..100000 for distance) — send a valid
     * dummy so the request passes; polygon catchment derives its area from
     * `locations[].catchment_area`, not from the contours value.
     */
    const contours = byRadius
      ? stringArrayToNumberArray(radiusData)
      : isDistance
        ? stringArrayToNumberArray(roadAccessConfig.distances)
        : stringArrayToNumberArray(roadAccessConfig?.durations);

    return {
      adm_area_id: undefined,
      catchment: {
        // For polygon catchment dataset, the profile/metric/exclusions config is not sent because it's not applicable.
        ...(isBVTDataForCatchmentEnabled && isPolygonCatchmentDataset
          ? {}
          : {
              // Required for Dataset Catchment POI
              profile:
                (byRadius
                  ? ECatchmentType.BY_RADIUS
                  : roadAccessConfig?.routingType) ??
                ECatchmentTransport.WALKING,
              exclusions: byRadius
                ? ([] as string[])
                : getEffectiveRoadExclusions(
                    roadAccessConfig?.roadExclusions,
                    roadAccessConfig?.routingType,
                    isNewRoadExclusionEnabled
                  ),
              metric: byRadius
                ? "distance"
                : (roadAccessConfig?.measurementType ?? ""),
              contours: contours
            }),
        locations,
        // Multi-dataset: send `datasets` array (BE rejects when set
        // together with `dataset`). Legacy: send `dataset` singular for
        // back-compat with old BE clients.
        ...(useMultiDataset
          ? { datasets: catchmentByDataset.map(buildCatchmentDatasetConfig) }
          : hasCatchmentDatasets
            ? { dataset: buildCatchmentDatasetConfig(catchmentByDataset[0]) }
            : {})
      },
      /** Mirror `mapAdminAreaInput`: round-trip the area-level global filter
       * flag so re-opening + re-saving a catchment project preserves whether
       * it respects the global admin area filter or overrides it.
       */
      is_global_admin_area: !overrideGlobalFilter
    };
  };

  const mapAdminAreaInput = (): AreaInput | null => {
    // When admin tree is enabled, use the first explicitly selected area ID
    if (isAdminTreeEnabled && adminTreeSettings?.selectedAreaIds?.length > 0) {
      return {
        adm_area_ids: adminTreeSettings.selectedAreaIds,
        is_global_admin_area: !overrideGlobalFilter
      };
    }

    // Fallback to dropdown-based selection
    const maxLevel = getMaxLevelAdminArea(adminAreaSettings);
    if (!maxLevel) return null;
    return {
      adm_area_id: adminAreaSettings?.[maxLevel]?.value,
      is_global_admin_area: !overrideGlobalFilter
    };
  };

  const mapCustomCatchmentInput = (): AreaInput | null => {
    if (Object.keys(polygonSettings).length === 0) return null;

    return {
      custom_catchments: {
        type: "FeatureCollection",
        features: Object.values(polygonSettings).map((polygon) => ({
          ...polygon.polygon
        }))
      },
      /** Mirror `mapAdminAreaInput`: round-trip the area-level global filter
       * flag so re-opening + re-saving a polygon project preserves whether
       * it respects the global admin area filter or overrides it.
       */
      is_global_admin_area: !overrideGlobalFilter
    };
  };

  const mappingAreaInput = (): AreaInput => {
    switch (spatialConfig.areaInput?.value) {
      case AreaInputType.CATCHMENT:
        /** BY_POLYGON catchment is sent as custom_catchments so BE accepts it
         * (a catchment with empty contours fails validation). Polygon
         * catchment DATASET (BY_DATASET) uses the same { catchment.datasets,
         * catchment.locations } shape as POI catchment dataset — handled by
         * mapCatchmentAreaInput which branches on dataset.spatial_aggregation_type.
         */
        if (
          isBVTDataForCatchmentEnabled &&
          catchmentSettings?.catchmentInputBy === ECatchmentInputBy.BY_POLYGON
        ) {
          return mapCustomCatchmentInput() ?? {};
        }
        return mapCatchmentAreaInput() ?? {};
      case AreaInputType.POLYGON:
        return mapCustomCatchmentInput() ?? {};
      default:
        return mapAdminAreaInput() ?? {};
    }
  };

  /* Advanced normalization is persisted as LOG_Z_SCORE on the scoring option, so
   * it round-trips. Same single signal used at submit time — no z_score check.
   */
  const isAdvancedNormalization =
    spatialConfig?.scoringOption === ScoringOptionsEnum.LOG_Z_SCORE;

  const mappingAnalysisOutput = (): AnalysisOutput | undefined => {
    const resolvedScoringOption = isAdvancedNormalization
      ? ScoringOptionsEnum.LOG_Z_SCORE
      : (spatialConfig.scoringOption ?? ScoringOptionsEnum.RAW);
    const outputZScore =
      isAdvancedNormalization && spatialConfig.intercept?.trim()
        ? {
            z_score: {
              intercept: parseFormattedNumber(spatialConfig.intercept)
            }
          }
        : {};
    // Output-level dasimetric opt-in — persisted so an opened config re-submits
    // with the same method. Only emitted when the feature is on and toggled.
    const dasimetryOutput =
      isDasimetryEnabled && spatialConfig.dasimetry
        ? { proportion_method: ProportionMethodOutput.DASYMETRIC }
        : {};

    if (!spatialConfig.outputType) {
      return {
        scoring_option: resolvedScoringOption,
        ...outputZScore,
        ...dasimetryOutput
      };
    }

    if (spatialConfig.outputType === EOutputAnalysisType.PROFILING)
      return {
        type: EOutputAnalysisType.PROFILING,
        scoring_option: resolvedScoringOption,
        ...outputZScore,
        ...dasimetryOutput
      };

    if (!spatialConfig.gridType) {
      return {
        scoring_option: resolvedScoringOption,
        ...outputZScore,
        ...dasimetryOutput
      };
    }

    const geohashType = spatialConfig.gridType === EOutputGridType.GEOHASH;
    const gridLevel = Number(
      geohashType
        ? (spatialConfig.gridResolution?.value ?? 0)
        : (spatialConfig.h3Resolution?.value ?? 0)
    );
    return {
      type: spatialConfig.gridType ?? "",
      grid_level: gridLevel,
      scoring_option: resolvedScoringOption,
      ...outputZScore,
      ...dasimetryOutput,
      ...buildCoverageTypePayload({
        isEnabled: isCoverageTypeEnabled,
        outputMode: spatialConfig.outputMode,
        radius: spatialConfig.customCatchmentRadius ?? 0,
        type: ECatchmentType.BY_RADIUS.toUpperCase()
      })
    };
  };

  // Filter datasets by acceptable spatial types
  const filterDatasetsByType = () => {
    return Object.entries(datasets).filter(([, dataset]) =>
      [
        "poi",
        "polygon",
        "adm_area",
        "line",
        EOutputAnalysisType.H3,
        EOutputAnalysisType.GEOHASH,
        "line"
      ].includes(dataset.properties.spatialType)
    );
  };

  const processDatasetAttributes = (
    datasetId: string
  ): DatasetConfigAttributeResponse[] => {
    const filtersArray = datasetFilters[datasetId] ?? [];
    const weightScore = weightValues[datasetId] ?? {};
    const filters: MappedFilter[] = filtersArray.map((filter) =>
      mapDatasetFilters(filter)
    );
    const currDataset = datasets[datasetId];
    const attributeSource =
      currDataset.properties.spatialType === "H3" &&
      currDataset.data.source === "bvt"
        ? currDataset.data.schema
        : currDataset.categories;
    const attributeSourceList = attributeSource ?? [];
    const isPoiDataset = currDataset.properties.spatialType === "poi";
    const primaryPoiAttribute = isPoiDataset
      ? getPrimaryPoiAttribute(attributeSourceList)
      : undefined;

    // Track matched filters to prevent the same filter from being matched to multiple attributes
    // This can happen when one attribute's id equals another's paired_key_attribute_id
    // Note: Admin area filters are excluded from this deduplication because they intentionally
    // match multiple schema items (adm_area_level_1, adm_area_level_2, etc.) and get deduplicated later
    const matchedFilterKeys = new Set<string>();

    return (
      attributeSourceList.map((attribute) => {
        const attributeId = getDatasetAttributeScoringId(attribute);
        let matchedIsAdmArea = false;

        const matchedFilter = filters.find((f) => {
          const isAdmAreaFilter = f.filter?.criteria?.includes("::");

          if (isAdmAreaFilter) {
            // Admin area filter has ::. For admin area filters, only check attribute id
            if (f.attributeId === attributeId) {
              matchedIsAdmArea = true;
              return true;
            }
          } else {
            // For non-admin-area filters, skip if already matched to prevent duplication
            const filterKey = `${f.attributeId}_${f.attributeCode ?? ""}`;
            if (matchedFilterKeys.has(filterKey)) return false;

            if (
              f.attributeId === attributeId &&
              f.attributeCode === attribute.attribute_code
            ) {
              matchedIsAdmArea = false;
              return true;
            }
          }
          return false;
        }) || { filter: null, filterType: null };

        // Mark non-admin-area filters as matched to prevent duplication
        if (matchedFilter.filter && !matchedIsAdmArea) {
          const filterKey = `${(matchedFilter as MappedFilter).attributeId}_${(matchedFilter as MappedFilter).attributeCode}`;
          matchedFilterKeys.add(filterKey);
        }

        const { filter, filterType } = matchedFilter;

        const attributeEntry = weightScore?.attributes?.[attributeId];
        const isDatasetIncluded = weightScore?.include_scoring !== false;

        const negativeImpact = getNegativeImpactValue(attributeEntry);
        const isPrimaryPoiAttribute =
          isPoiDataset && attribute === primaryPoiAttribute;
        // POI datasets: only the primary "Total POI Count" attribute follows
        // the dataset switch. Non-POI attributes keep their own toggles.
        const includeScoring = isPoiDataset
          ? isPrimaryPoiAttribute && isDatasetIncluded
          : isDatasetIncluded &&
            isNumericAttribute(attribute) &&
            getIsIncludedScoring(attributeEntry);

        // z_score persisted only under advanced normalization, and only for
        // included weights (BE: excluded weights need no log_mean/log_std_dev).
        const zScore =
          isAdvancedNormalization && includeScoring
            ? parseZScore(attributeEntry)
            : undefined;

        return {
          filter,
          id: attributeId,
          weight_score: parseWeightValue(attributeEntry),
          ...(negativeImpact !== undefined
            ? {
                weight_negative_impact: negativeImpact
              }
            : {}),
          include_scoring: includeScoring,
          ...(zScore ? { z_score: zScore } : {}),
          ...(filterType ? { filterType } : {}),
          ...(attribute.attribute_code
            ? { attribute_code: attribute.attribute_code }
            : {})
        };
      }) || []
    );
  };

  /**
   * Uploads any not-yet-uploaded legend marker images (data URLs, no asset id)
   * across all datasets' Simple / Custom Ordinal / Custom Breaks configs and
   * returns an `imageUrl → assetId` map (ORB-3333). Images are uploaded on save;
   * `mappingDataset(map)` then persists the resolved `fill_image_asset_id`.
   *
   * Throws when any upload fails — the caller must abort the save/run so a
   * config is never persisted with rows missing their asset ids. Successful
   * ids are stamped onto the store first, so a retry only re-uploads the
   * failed images.
   *
   * `datasetsOverride` picks the dataset map to collect from — the post-analysis
   * save needs the result datasets, not the history snapshot used below.
   */
  const uploadPendingLegendImages = async (
    datasetsOverride?: Record<string, Dataset | undefined>
  ): Promise<Map<string, string>> => {
    const assetIdByImageUrl = new Map<string, string>();

    // Collect the unique pending data URLs (deduped) with a filename, plus a
    // `url → assetId` map of images some other row already uploaded, so a
    // re-applied image (e.g. from the Recent list) reuses its id instead of
    // hitting the upload API again.
    const pending = new Map<string, string>();
    const knownAssetIds = new Map<string, string>();
    const collect = (
      img?: {
        imageUrl: string | null;
        imageName: string | null;
        imageAssetId: string | null;
      } | null
    ) => {
      if (!img?.imageUrl) return;
      if (img.imageAssetId) {
        knownAssetIds.set(img.imageUrl, img.imageAssetId);
      } else if (isLocalImageUrl(img.imageUrl) && !pending.has(img.imageUrl)) {
        pending.set(img.imageUrl, img.imageName ?? "image");
      }
    };
    Object.values(datasetsOverride ?? datasets).forEach((dataset) => {
      const style = dataset?.style;
      collect(style?.singleStyleConfig);
      collect(style?.singleStyleConfigSaved);
      style?.customCategoryConfig?.steps.forEach(collect);
      style?.customCategoryConfigSaved?.steps.forEach(collect);
      style?.customRangeConfig?.steps.forEach(collect);
      style?.customRangeConfigSaved?.steps.forEach(collect);
    });
    [...pending.keys()].forEach((dataUrl) => {
      const knownId = knownAssetIds.get(dataUrl);
      if (knownId) {
        assetIdByImageUrl.set(dataUrl, knownId);
        pending.delete(dataUrl);
      }
    });
    if (!pending.size && !assetIdByImageUrl.size) return assetIdByImageUrl;

    let hasUploadFailure = false;
    await Promise.all(
      [...pending].map(async ([dataUrl, name]) => {
        const blob = dataUrlToBlob(dataUrl);
        if (!blob) return;
        try {
          const { id } = await uploadAssetAsync({ file: blob, filename: name });
          assetIdByImageUrl.set(dataUrl, id);
        } catch {
          hasUploadFailure = true;
        }
      })
    );

    // Stamp the resolved ids back onto the store so the SAME image isn't
    // re-uploaded on a subsequent save (the collector skips rows that already
    // carry an `imageAssetId`), and onto the localStorage Recent list so a
    // later re-apply from Recent carries the id instead of re-uploading.
    if (assetIdByImageUrl.size) {
      dispatch(
        applyLegendImageAssetIds({
          assetIdByImageUrl: Object.fromEntries(assetIdByImageUrl)
        })
      );
      applyRecentMarkerImageAssetIds(assetIdByImageUrl);
    }
    if (hasUploadFailure) {
      throw new Error("Failed to upload legend image");
    }
    return assetIdByImageUrl;
  };

  const mappingDataset = (
    assetIdByImageUrl?: Map<string, string>
  ): DatasetConfigResponse[] => {
    if (!Object.keys(datasets).length) return [];

    const filteredDatasets = filterDatasetsByType();

    // Optimize lookup by creating a Map for O(1) access
    const datasetMap = new Map(filteredDatasets);

    const orderedFilteredDatasets = visualizationOrder
      .filter((datasetId) => datasetMap.has(datasetId))
      .map(
        (datasetId) =>
          [datasetId, datasetMap.get(datasetId)] as [
            string,
            Dataset | undefined
          ]
      )
      .filter((entry): entry is [string, Dataset] => entry[1] !== undefined);

    return orderedFilteredDatasets.map(([datasetId, dataset]) => {
      const attributes = processDatasetAttributes(datasetId);

      // nonAdminAttributes can have non-unique attribute id
      // Thus, the Set cannot be used to filter them
      const { adminAreaAttributes, nonAdminAttributes } =
        separateAdminAreaAttributes(attributes, dataset);

      const finalAttributes = createFinalAttributes(
        nonAdminAttributes,
        adminAreaAttributes
      );

      // Lines carry their opacity on the stroke, other geometries on the fill.
      const opacity =
        dataset.properties.spatialType === "line"
          ? dataset?.style?.stroke?.opacity
          : dataset?.style?.fillOpacity;

      return {
        id: removeStartFromUnderscore(datasetId),
        display_name: dataset?.data?.display_name,
        spatial_aggregation_type: dataset.properties.spatialType,
        attributes: finalAttributes,

        color_based: dataset?.selectedCategory,
        color_scale: dataset?.style?.colorFamily,
        opacity,
        grid_config: getGridConfig(dataset),
        color_steps: dataset?.style?.colorSteps,
        stroke_width:
          dataset.properties.spatialType === "line"
            ? dataset?.style?.stroke?.width
            : undefined,
        label_config: {
          ...dataset?.style?.labelConfig,
          enable_ranking: dataset?.style?.rankConfig?.isEnabled ?? false,
          max_ranking: dataset?.style?.rankConfig?.maxRanking ?? 10
        },
        weight: parseWeightValue(weightValues[datasetId]?.weight),
        include_scoring: weightValues[datasetId]?.include_scoring !== false,
        is_global_admin_area: dataset.is_global_admin_area ?? false,
        ...(dataset.group_by === "none" ? {} : { group_by: dataset.group_by }),
        // Custom Map Visualization & Legend (ORB-3246): persist simple /
        // custom_ordinal legend config. Covers before & after analysis — the
        // `dataset` source above already switches to the history snapshot.
        ...(isCustomMapVisualizationLegendEnabled
          ? mapStyleToLegend(dataset?.style, assetIdByImageUrl, opacity)
          : {})
      };
    });
  };

  const mappingAdmAreaIds = (): string[] => {
    if (isAdminTreeEnabled) {
      return globalSelectedAreaIds;
    }
    const maxLevel = getMaxLevelAdminArea(globalAdminAreaSettings);
    if (!maxLevel) return [];

    const value = globalAdminAreaSettings?.[maxLevel]?.value;
    return value ? [value] : [];
  };

  const mappingWidgets = (): WidgetDefaultConfiguration[] => {
    if (!isDatasetWidgetEnabled) return [];

    // Preserve the user's widget ordering; fall back to the map keys when the
    // order list is empty. Skip ids that no longer have a backing widget.
    const orderedWidgetIds = (
      visualizationOrderWidget.length
        ? visualizationOrderWidget
        : Object.keys(widgets)
    ).filter((widgetId) => widgets[widgetId]);

    return orderedWidgetIds.map((widgetId) => {
      const widget = widgets[widgetId];
      const sourceDataset = datasets[widget.dataset_id];

      // grid_config is derived from the source dataset's h3Configuration — same
      // aggregation/level shape (and AVG/8 defaults) as getGridConfig uses for
      // datasets, so H3 widgets serialize consistently with their dataset.
      const gridConfig = sourceDataset?.h3Configuration
        ? {
            aggregation_type:
              sourceDataset.h3Configuration.aggregationType ??
              AggregationType.AVG,
            level: sourceDataset.h3Configuration.resolution ?? 8
          }
        : undefined;

      return {
        widget_id: widget.widget_id,
        is_default: widget.is_default,
        dataset_id: removeStartFromUnderscore(widget.dataset_id),
        label: widget.label, // dataset name used as the widget title
        chart_type: widget.chart_type,
        dimension: widget.dimension, // inferenced from dataset_schema.paired_key_attr_id
        measure: widget.measure, // synthetic record_count for POI count
        aggregation: widget.aggregation,
        configuration: { ...widget.configuration },
        ...(gridConfig ? { grid_config: gridConfig } : {}),
        ...(sourceDataset?.group_by && sourceDataset?.group_by === "none"
          ? {}
          : { group_by: sourceDataset?.group_by })
      };
    });
  };

  return {
    getGridConfig,
    mappingDataset,
    uploadPendingLegendImages,
    mappingAreaInput,
    mappingAdmAreaIds,
    mappingAnalysisOutput,
    mappingWidgets
  };
};

export default useConfigurationMappers;
